/**
 * creative API の oRPC クライアント
 *
 * `@cortex/domain-creative` の contract から型安全な RPC クライアントを生成し、
 * `@orpc/tanstack-query` で useQuery / useMutation 用ユーティリティ (`orpc`) を公開する。
 * 本番は `VITE_API_URL` (https://api-creative.air-closet.ai) の `/rpc` へ、dev は
 * Vite proxy の `/api/rpc` へ送る。Edge Auth Cookie を送るため `credentials: 'include'` を
 * 付与し、401 + `X-Edge-Auth-Start` を受けたときは OAuth start へリダイレクトする。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business creative web から creative API を呼び出す oRPC クライアント。contract-first で型安全に通信し、401 時は OAuth start へ誘導する。
 * @graph-connects api [calls] creative API (oRPC /rpc) の呼び出し
 */
import { creativeContract, type CreativeAuthUser } from '@cortex/domain-creative/web';
import { createORPCClient, safe } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import type { ContractRouterClient } from '@orpc/contract';
import { createTanstackQueryUtils } from '@orpc/tanstack-query';

/** API のベース URL。本番は VITE_API_URL、dev は Vite プロキシの `/api`。末尾スラッシュは除去。 */
/** @graph-connects none */
const API_BASE = new URL(import.meta.env.VITE_API_URL ?? '/api', window.location.origin)
  .toString()
  .replace(/\/$/, '');

/**
 * oRPC RPC リンク。credential 付き fetch で `/rpc` を叩き、401 + `X-Edge-Auth-Start` を
 * 受けたら OAuth start へリダイレクトする。
 * @graph-connects api [calls] creative API (oRPC /rpc) の呼び出し
 */
const link = new RPCLink({
  url: `${API_BASE}/rpc`,
  fetch: async (request, init) => {
    const response = await fetch(request, { ...init, credentials: 'include' });
    if (response.status === 401) {
      const authStart = response.headers.get('X-Edge-Auth-Start');
      if (authStart !== null && authStart !== '' && typeof window !== 'undefined') {
        window.location.assign(authStart);
      }
    }
    return response;
  },
});

/** contract から型付けされた oRPC クライアント。 @graph-connects none */
export const client: ContractRouterClient<typeof creativeContract> = createORPCClient(link);

/**
 * TanStack Query 用の oRPC ユーティリティ。
 * `orpc.<namespace>.<procedure>.queryOptions({ input })` / `.mutationOptions()` を提供する。
 * @graph-connects none
 */
export const orpc = createTanstackQueryUtils(client);

/**
 * 現在のログインユーザーと編集権限を取得する。未ログインは null を返し throw しない。
 * @returns ログインユーザー + 権限、または null
 * @graph-connects api [calls] 認証状態取得 API
 */
export async function getCurrentUser(): Promise<CreativeAuthUser | null> {
  const [error, data] = await safe(client.auth.me({}));
  if (error) {
    return null;
  }
  return data;
}
